import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  DropdownField,
  FormSection,
  TextAreaField,
  useToast,
} from "../../../components/ui";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import { SUPPORT_REASONS } from "../data/supportReasons";
import ContactUsCard from "./ContactUsCard";
import SupportHistory, { formatMeetingWhen } from "./SupportHistory";
import { useSupportHistory } from "../hooks/useSupportHistory";
import { isUpcomingStatus } from "../types/support.types";
import { ROUTES } from "../../../routes/paths";

/**
 * Settings → Support: booking a meeting, writing to us, and what has already
 * been booked.
 *
 * A written enquiry once stood beside the booking form as a second form of its
 * own, and was taken out: no endpoint takes one, and a form that quietly
 * reached nobody was worth less than not offering it. What is beside the booker
 * now is our address in plain text — see `ContactUsCard` — which is the same
 * offer made in the one way the panel can honestly make it, since an address
 * needs nothing of ours, and nothing of the machine's, to be acted on.
 *
 * Neither is the whole tab. Booking goes through Cal against a connected
 * calendar and is the thing this tab is for; the address is for everything that
 * does not need half an hour of somebody's diary. So they share the row, and a
 * reason and a paragraph are not stretched across the screen either way.
 *
 * Under it, what this workspace has actually booked — read from
 * `GET /api/v1/support-history/`, which is our backend relaying Cal's own
 * record of it. Those rows do two jobs: they are the history, and they are what
 * the form checks when a second call is asked for. See `bookedCall`.
 */
const SupportTab: React.FC = () => {
  const { firstName } = useSignedInUser();
  const navigate = useNavigate();
  const toast = useToast();

  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");

  const {
    meetings,
    status: historyStatus,
    error: historyError,
    reload: reloadHistory,
  } = useSupportHistory();

  // One call at a time. A second booking made while the first is still ahead is
  // nearly always somebody who meant to move it, and it costs two slots and one
  // no-show to find that out — so the booking screen is not opened over a call
  // that is already standing, and pressing the button says so instead.
  //
  // Only a loaded history closes the form. While it is coming, or where it
  // could not be read, there is no booking to point at — and a dietitian shut
  // out of support by a network error has no other way to reach us, so the
  // uncertain case leaves the form open rather than shut.
  //
  // A call that has been moved counts as booked, like any other still ahead —
  // it is one slot in one diary either way, and asked of the status rather than
  // spelled out here so the two words stay one rule. See `isUpcomingStatus`.
  //
  // The call itself and not merely that there is one: the form has to name it,
  // since "you already have a call booked" is only half an answer without the
  // day it is on. The soonest of them where there is somehow more than one —
  // that is the one the reader is about to turn up to, and the one they mean.
  const bookedCall = useMemo(() => {
    if (historyStatus !== "ready") return undefined;

    return meetings
      .filter((meeting) => isUpcomingStatus(meeting.status))
      .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0];
  }, [historyStatus, meetings]);

  // Both answers, because neither is any use alone: a reason with nothing under
  // it says only which queue to join, and a paragraph with no reason has to be
  // read by somebody before it can be routed.
  const ready = reason !== "" && message.trim() !== "";

  const alreadyBooked = Boolean(bookedCall);

  /**
   * Off to the booker, carrying what they wrote.
   *
   * In router state rather than the URL: a reason and a message are exactly the
   * descriptive detail that `paths` keeps out of a path, and neither belongs in
   * a browser history or a screenshot of the address bar. The booking screen
   * opens unfilled if it is ever reached without them.
   */
  const handleBookCall = () =>
    navigate(ROUTES.bookCall, {
      state: { reason, message: message.trim() },
    });

  /**
   * Why the button will not go, given when it is pressed.
   *
   * The rule is worth a whole sentence and the button has room for a state, so
   * the two are split: "Meeting scheduled" on the face says the outcome, and
   * this says the reason — but only to somebody who reached for it, rather than
   * standing over an untouched form where it would be read as furniture. It
   * names the call and the day it is on, since "you already have one booked" is
   * half an answer: the reader's next question is which one, and the row it
   * points at is below this card.
   *
   * Handed to `Button` as `onDisabledClick`, which is what keeps a disabled
   * button reachable enough to answer — see the note in that component.
   */
  const explainBookedCall = () => {
    if (!bookedCall) return;

    toast.error(
      "You already have a support call booked",
      `It is on ${formatMeetingWhen(bookedCall.startsAt)}. Only one call can be open at a time — move it with Modify under Support history, or book again once it is done.`
    );
  };

  return (
    <div>
      {/* Addressed to whoever is reading it. Clerk holds the name and has it in
          memory, so this costs no request; the greeting simply drops the name
          for the moment before the session is restored, rather than saving a
          space for one that is not there yet. */}
      <h2 className="text-[17px] sm:text-[18px] 3xl:text-[20px] font-medium text-ink">
        {firstName ? `Hey ${firstName}, ` : "Hey, "}How can we help you?
      </h2>

      {/* The two ways of reaching us, side by side from `lg`: the booker on the
          left, and writing to us on the right. Stacked below that, booker
          first — a call is the thing this tab is for, and the address is what
          somebody falls back to. */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2 3xl:gap-6">
        <FormSection title="Book a meeting with us">
          <form
            // Named after the caption above it, so a screen reader meets the
            // fields as this form's rather than as a loose list of controls.
            aria-label="Book a meeting with us"
            className="flex flex-col text-[14px] 3xl:text[16px] font-medium gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!ready || alreadyBooked) return;
              handleBookCall();
            }}
          >
            <DropdownField
              label="Choose your reason"
              placeholder="eg. Account setup"
              options={SUPPORT_REASONS}
              value={reason}
              onValueChange={setReason}
            />

            <TextAreaField
              placeholder="Explain your issue"
              rows={3}
              value={message}
              onValueChange={setMessage}
            />

            {/* Nothing is in flight when this is pressed — the button is a way
                to the booking screen rather than a request, and a spinner on a
                link is a lie. With a call already booked it stops being an
                action at all and becomes the sentence that says so.

                Shut either way, but only one of the two has anything to add. An
                unfinished form is answered by the fields themselves — the
                reason and the paragraph are visibly empty, and a message saying
                so tells the reader what they can already see. A call already
                booked is not on this card at all, so pressing it is answered:
                `onDisabledClick` is what keeps a disabled button reachable
                enough to give a reason. See `explainBookedCall`. */}
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={alreadyBooked || !ready}
              onDisabledClick={alreadyBooked ? explainBookedCall : undefined}
              className="self-start min-w-[130px]"
            >
              {alreadyBooked ? "Meeting scheduled" : "Book call"}
            </Button>
          </form>
        </FormSection>

        {/* Dropped by the height of the caption opposite — `FormSection` gives
            every one an `h-8` heading over an `mb-2`, which is the 40px here —
            so both columns' cards start on the same line. This card has no
            caption of its own: it is one thing rather than a labelled group,
            and a made-up caption over it to square the columns would read as a
            second section on a tab that has one.

            Only from `lg`, where the two are side by side. Stacked below that
            there is nothing to line up with and the grid's own gap is the
            spacing. */}
        <div className="lg:mt-10">
          <ContactUsCard />
        </div>
      </div>

      {/* Full width under the form rather than beside it: a row is an icon, a
          date and two buttons, and half the screen leaves the buttons sitting
          on top of the date on anything narrower than a laptop. */}
      <SupportHistory
        meetings={meetings}
        loading={historyStatus === "loading"}
        error={historyStatus === "failed" ? historyError : undefined}
        onRetry={reloadHistory}
      />
    </div>
  );
};

export default SupportTab;
